import React, { useEffect, useState } from 'react'

import type { DropdownProps } from '@space-lib/components/dropdown/types'
import DropdownUser from '@space-lib/components/dropdown/user'

import { StringUtils } from '@space-lib/utils/string'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import useMembership from '@space-shell/common/hooks/useMembership'
import { IS_FIRST_ACCESS_TO_PROFILE, useUserInfoStore } from '@space-shell/core/store'
import { useUserProfileEvents } from '@space-shell/modules/communities/hooks'
import { useTranslation } from 'react-i18next'

import { AVATAR_TOOLTIP_STORAGE_ITEM, HIDE_AVATAR_TOOLTIP_TIMEOUT } from './constants'

const Dropdown = ({ userName, dropdownItems, isLogged, theme, children }: DropdownProps) => {
  const { avatar, email } = useUserInfoStore()
  const { sendDataHubEvent } = useDataHubEvents()
  const { membershipData } = useMembership()
  const [hasStorageFirstAccess, setHasStorageFirstAccess] = useState(
    !!localStorage.getItem(IS_FIRST_ACCESS_TO_PROFILE)
  )
  const { onOpenProfile } = useUserProfileEvents()
  const { t } = useTranslation()

  const hasTooltipAvatarLocalStorageItem = localStorage.getItem(AVATAR_TOOLTIP_STORAGE_ITEM)
  const [showTooltipAvatar, showSetTooltipAvatar] = useState(!hasTooltipAvatarLocalStorageItem)

  const handleOpenProfile = (event: React.MouseEvent | React.KeyboardEvent) => {
    onOpenProfile(event)
  }

  const avatarTooltipText = isLogged && showTooltipAvatar ? t('view_as.profile.tooltip') : undefined
  const profileButtonLabel = t('general.access_profile')
  const avatarHighlight = Boolean(!hasStorageFirstAccess)

  const handleDisableAvatarHighlight = () => {
    setHasStorageFirstAccess(true)
  }

  useEffect(() => {
    if (avatarTooltipText) {
      const timeoutId = setTimeout(() => {
        showSetTooltipAvatar(false)
        localStorage.setItem(AVATAR_TOOLTIP_STORAGE_ITEM, 'true')
      }, HIDE_AVATAR_TOOLTIP_TIMEOUT)

      return () => clearTimeout(timeoutId)
    }

    return undefined
  }, [avatarTooltipText])

  return (
    <DropdownUser
      dropdownItems={dropdownItems}
      email={email}
      initialLetters={StringUtils.getFirstLettersName(String(userName))}
      userName={StringUtils.getFirstNameLastName(String(userName))}
      urlAvatar={avatar}
      sendDataHubEvent={sendDataHubEvent}
      customColor={membershipData?.settings.primaryColor}
      avatarTooltipText={avatarTooltipText}
      avatarHighlight={avatarHighlight}
      theme={theme}
      isLogged={isLogged}
      onDisableAvatarHighlight={handleDisableAvatarHighlight}
      profileButtonLabel={profileButtonLabel}
      onClickProfile={handleOpenProfile}>
      {children}
    </DropdownUser>
  )
}

export default Dropdown
